"use client";

import * as React from "react";
import { ArrowRight, ShoppingCart } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const filterTabs = [{
  value: "all",
  label: "ALL"
}, {
  value: "peptides",
  label: "PEPTIDES"
}, {
  value: "stacks",
  label: "STACKS"
}, {
  value: "research",
  label: "RESEARCH"
}, {
  value: "supplies",
  label: "SUPPLIES"
}];
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-2 gap-px bg-border md:grid-cols-3 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="aspect-[3/4] bg-card animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="border border-border p-12 text-center">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          Connection to laboratory server failed.
        </p>
        <Button variant="outline" onClick={refetch} className="mt-4 rounded-md border-border font-mono text-xs uppercase tracking-widest">
          Reconnect
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="border border-border p-12 text-center">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          No compounds currently in stock.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
      {products.map((product, i) => {
      const skuCode = `IH-${String(product.id).padStart(4, "0")}`;
      const isNew = i % 3 === 0;
      return <div key={product.id} data-index={i} className="group relative flex flex-col border border-border bg-card/40 backdrop-blur-sm shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
            {}
            <div className="absolute inset-x-0 top-0 h-0.5 bg-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

            {}
            <Link to={product.link || "#"} className="relative block">
              <div className="relative aspect-square w-full overflow-hidden bg-secondary">
                {}
                <Badge className="absolute left-3 top-3 z-10 rounded-md bg-primary px-2 py-1 font-mono text-[10px] font-medium uppercase tracking-widest text-primary-foreground">
                  {isNew ? "NEW BATCH" : "LAB TESTED"}
                </Badge>

                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center">
                    <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                      Compound Image
                    </span>
                  </div>}

                {}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/40" />
              </div>
            </Link>

            {}
            <div className="flex flex-1 flex-col gap-3 p-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                LOT / {skuCode}
              </div>

              <Link to={product.link || "#"}>
                <h3 className="font-default text-lg font-bold uppercase leading-tight tracking-tight text-foreground transition-colors hover:text-primary">
                  {product.title}
                </h3>
              </Link>

              {product.short_description ? <div className="font-sans text-sm leading-snug text-muted-foreground line-clamp-1 [&_p]:m-0" dangerouslySetInnerHTML={{
            __html: product.short_description
          }} /> : <p className="font-sans text-sm leading-snug text-muted-foreground">
                  High-purity research grade peptide.
                </p>}

              <div className="mt-auto flex items-center justify-between gap-3 pt-4">
                <div className="font-default text-xl font-bold text-primary">
                  ${product.price ?? "—"}
                </div>

                <div className="relative">
                  <AddToCartButton productId={product.id} variant="default" size="sm" text="ADD" showQuantity={false} className="w-full" buttonClassName="bg-primary text-primary-foreground rounded-md font-mono text-xs font-medium uppercase tracking-widest px-4 hover:bg-primary/90" />
                </div>
              </div>
            </div>
          </div>;
    })}
    </div>;
}
function ProductsWithFilters() {
  return <Tabs defaultValue="all" className="w-full">
      <div className="mb-12 flex flex-col gap-10 border-b border-border pb-6 lg:flex-row lg:items-end lg:justify-between">
        {}
        <div className="flex flex-col gap-4">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
            INDEX 01 / COMPOUNDS
          </div>
          <h2 className="font-default text-5xl font-bold uppercase leading-[0.95] tracking-tighter text-foreground md:text-6xl lg:text-7xl">
            Featured
            <br />
            Compounds
          </h2>
          <p className="font-sans text-base italic text-muted-foreground">
            Elite-grade research materials
          </p>
        </div>

        {}
        <TabsList className="h-auto flex-wrap justify-start gap-0 rounded-md bg-transparent p-0">
          {filterTabs.map((tab, i) => <TabsTrigger key={tab.value} data-index={i} value={tab.value} className="relative rounded-md border-0 bg-transparent px-4 py-3 font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground transition-colors data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:after:absolute data-[state=active]:after:inset-x-3 data-[state=active]:after:-bottom-[25px] data-[state=active]:after:h-0.5 data-[state=active]:after:bg-primary">
              {tab.label}
            </TabsTrigger>)}
        </TabsList>
      </div>

      {}
      <ProductsGridConsumer />

      {}
      <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-border pt-8 sm:flex-row sm:items-center">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          [ BATCH VERIFIED — 2024 ]
        </div>
        <Link to="/ShopPage">
          <Button variant="outline" className="group rounded-md border-border bg-transparent font-mono text-xs font-medium uppercase tracking-widest text-foreground hover:bg-primary hover:text-primary-foreground">
            View Full Catalog
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </Link>
      </div>
    </Tabs>;
}
export default function FeaturedCompounds() {
  const wp_query_params = {
    per_page: 8,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return;
}